import React, { useMemo, useState } from 'react'
import './TagsStyle.css'
import {
  ActionButtons,
  Button,
  DataTable,
  EmptyState,
  InputField,
  Modal,
  SearchWithFilter,
  type DataTableColumn,
  type DataTableSortDirection,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as DeleteIcon } from '@enamelai/design-system/icons/Delete2'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import { Icon as TagIcon } from '@enamelai/design-system/icons/Tag'
import { type EmailTag } from '../useEmail'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'

const TAG_COLOR_OPTIONS = [
  { label: 'Coral', value: '#FE746B' },
  { label: 'Orange', value: '#E46910' },
  { label: 'Yellow', value: '#EDCA31' },
  { label: 'Lime', value: '#A4CC6B' },
  { label: 'Green', value: '#6ABE81' },
  { label: 'Teal', value: '#2DADA9' },
  { label: 'Blue', value: '#185DD8' },
  { label: 'Indigo', value: '#3A3AE9' },
]

const TAG_COLORS = TAG_COLOR_OPTIONS.map((option) => option.value)

const DEFAULT_PAGE_SIZE = 10

type TagEditorState = {
  id: string | null
  name: string
  color: string
}

type TagTableRow = { id: string }

type TagSortColumn = 'name' | 'color'

const EMPTY_EDITOR: TagEditorState = {
  id: null,
  name: '',
  color: TAG_COLORS[0],
}

type TagsTabProps = {
  tags: EmailTag[]
  onRemoveTag: (id: string) => void
  onCreateTag: (name: string, color: string) => Promise<void>
  onUpdateTag: (id: string, name: string, color: string) => Promise<void>
}

const compareText = (
  left: string,
  right: string,
  direction: DataTableSortDirection,
): number => {
  const result = left.localeCompare(right)
  return direction === 'asc' ? result : -result
}

const TagsTab = ({
  tags,
  onRemoveTag,
  onCreateTag,
  onUpdateTag,
}: TagsTabProps): React.ReactElement => {
  const [editor, setEditor] = useState<TagEditorState | null>(null)
  const [saving, setSaving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<TagSortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [colorFilters, setColorFilters] = useState<FilterSelection>({})
  const [pendingDelete, setPendingDelete] = useState<EmailTag | null>(null)

  const openCreate = (): void => setEditor({ ...EMPTY_EDITOR })
  const openEdit = (tag: EmailTag): void =>
    setEditor({ id: tag.id, name: tag.name, color: tag.color })
  const closeEditor = (): void => setEditor(null)

  const handleSubmit = async (): Promise<void> => {
    if (!editor || !editor.name.trim()) return
    setSaving(true)
    try {
      if (editor.id) {
        await onUpdateTag(editor.id, editor.name, editor.color)
      } else {
        await onCreateTag(editor.name, editor.color)
      }
      closeEditor()
    } finally {
      setSaving(false)
    }
  }

  const tagsById = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags],
  )

  const handleSort = (columnId: string): void => {
    const column = columnId as TagSortColumn
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const sortedTags = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const selectedColors = new Set(
      TAG_COLOR_OPTIONS.filter((option) =>
        (colorFilters.color ?? []).includes(option.label),
      ).map((option) => option.value.toLowerCase()),
    )
    const filtered = tags.filter((tag) => {
      if (query && !tag.name.toLowerCase().includes(query)) return false
      if (
        selectedColors.size > 0 &&
        !selectedColors.has(tag.color.toLowerCase())
      ) {
        return false
      }
      return true
    })
    if (!sortColumnId) return filtered
    return [...filtered].sort((left, right) =>
      compareText(
        sortColumnId === 'name' ? left.name : left.color,
        sortColumnId === 'name' ? right.name : right.color,
        sortDirection,
      ),
    )
  }, [tags, sortColumnId, sortDirection, searchQuery, colorFilters])

  const totalPages = Math.max(1, Math.ceil(sortedTags.length / pageSize))
  const pageNumber = Math.min(currentPage, totalPages)
  const pageRows = useMemo(
    (): TagTableRow[] =>
      sortedTags
        .slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
        .map((tag) => ({ id: tag.id })),
    [sortedTags, pageNumber, pageSize],
  )

  const renderTag =
    (render: (tag: EmailTag) => React.ReactNode) =>
    (row: TagTableRow): React.ReactNode => {
      const tag = tagsById.get(row.id)
      return tag ? render(tag) : null
    }

  const columns: DataTableColumn<TagTableRow>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '45%',
      sortable: true,
      renderCell: renderTag((tag) => tag.name),
    },
    {
      id: 'color',
      label: 'Color',
      width: '35%',
      renderCell: renderTag((tag) => (
        <span
          className="scp-email-tags-color"
          style={{ ['--label-color' as string]: tag.color }}
        >
          <TagIcon className="scp-email-tags-color-icon" aria-hidden="true" />
        </span>
      )),
    },
  ]

  return (
    <div className="scp-email-tags">
      <div className="scp-table-group">
        <div className="scp-table-title-container">
          <span className="scp-table-title">
            Email labels
            <span
              className="ds-section-card__icon-button"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title="Manage labels used to organize clinic email."
            >
              <InfoIcon aria-hidden="true" />
            </span>
          </span>
          {tags.length > 0 && (
            <div className="scp-email-tags-toolbar">
              <SearchWithFilter
                placeholder="Search labels"
                value={searchQuery}
                onChange={(value) => {
                  setSearchQuery(value)
                  setCurrentPage(1)
                }}
                filters={{
                  color: TAG_COLOR_OPTIONS.map((option) => option.label),
                }}
                filterLabels={{ color: 'Color' }}
                selectedFilters={colorFilters}
                onFilterChange={(next) => {
                  setColorFilters(next)
                  setCurrentPage(1)
                }}
                showResetButton
                onReset={() => {
                  setSearchQuery('')
                  setColorFilters({})
                  setCurrentPage(1)
                }}
              />
              <Button
                size="sm"
                type="button"
                variant="primary"
                onClick={openCreate}
              >
                Add label
              </Button>
            </div>
          )}
        </div>

        {editor && (
          <div className="scp-email-tags-editor">
            <div className="scp-email-tags-editor-field">
              <InputField
                variant="text"
                label="Label name"
                fullWidth
                placeholder="Enter label name"
                value={editor.name}
                onChange={(e) =>
                  setEditor((prev) =>
                    prev ? { ...prev, name: e.target.value } : prev,
                  )
                }
              />
            </div>
            <div className="scp-email-tags-editor-colors">
              <span className="scp-email-tags-editor-label">Color</span>
              <div className="scp-email-tags-swatches">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`scp-email-tags-swatch${
                      editor.color === color ? ' is-selected' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Select color ${color}`}
                    onClick={() =>
                      setEditor((prev) => (prev ? { ...prev, color } : prev))
                    }
                  />
                ))}
              </div>
            </div>
            <div className="scp-email-tags-editor-actions">
              <Button
                size="sm"
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={closeEditor}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="button"
                variant="primary"
                disabled={saving || !editor.name.trim()}
                onClick={() => {
                  void handleSubmit()
                }}
              >
                {editor.id ? 'Save' : 'Add'}
              </Button>
            </div>
          </div>
        )}

        {tags.length === 0 || sortedTags.length === 0 ? (
          <EmptyState
            illustration={<TagIcon aria-hidden="true" />}
            title={tags.length === 0 ? 'No labels yet' : 'No matching labels'}
            description={
              tags.length === 0
                ? 'Add a label to organize clinic email.'
                : 'No labels match your search.'
            }
            actionLabel={tags.length === 0 ? 'Add label' : undefined}
            onAction={openCreate}
          />
        ) : (
          <DataTable<TagTableRow>
            className="scp-email-tags-grid"
            rows={pageRows}
            columns={columns}
            showActions={true}
            renderActions={renderTag((tag) => (
              <ActionButtons
                actions={[
                  {
                    id: 'edit',
                    label: `Edit ${tag.name}`,
                    icon: (
                      <EditIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    ),
                    tone: 'primary',
                    variant: 'outline',
                    onClick: () => openEdit(tag),
                  },
                  {
                    id: 'delete',
                    label: `Delete ${tag.name}`,
                    icon: (
                      <DeleteIcon
                        className="ds-action-button__svg"
                        aria-hidden="true"
                      />
                    ),
                    tone: 'danger',
                    variant: 'outline',
                    onClick: () => setPendingDelete(tag),
                  },
                ]}
              />
            ))}
            sortColumnId={sortColumnId ?? undefined}
            sortDirection={sortColumnId ? sortDirection : undefined}
            onSort={handleSort}
            pagination={
              sortedTags.length === 0
                ? undefined
                : {
                    currentPage: pageNumber,
                    totalPages,
                    pageSize,
                    onPageChange: setCurrentPage,
                    onPageSizeChange: (size) => {
                      setPageSize(size)
                      setCurrentPage(1)
                    },
                  }
            }
          />
        )}
        <Modal
          open={pendingDelete !== null}
          onClose={() => setPendingDelete(null)}
          closeOnBackdrop={false}
          size="sm"
          variant="confirmation"
          title="Delete label"
          icon={<DeleteIcon width={16} height={16} aria-hidden="true" />}
          iconTone="danger"
          description={`Are you sure you want to delete ${pendingDelete?.name ?? 'this'} label? This cannot be undone.`}
          primaryLabel="Delete"
          secondaryLabel="Cancel"
          primaryDestructive
          onPrimaryClick={() => {
            if (pendingDelete) onRemoveTag(pendingDelete.id)
            setPendingDelete(null)
          }}
          onSecondaryClick={() => setPendingDelete(null)}
        />
      </div>
    </div>
  )
}

export default TagsTab
